import * as vp from 'vopay';
import * as Yup from 'yup';
import { useState, useRef, FunctionComponent } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Formik } from 'formik';
import styles from '../Login.module.scss';
import { Button } from 'UI';
import { useDataFetchIndicator } from '@/hooks';
import useRequestClientPasswordReset from '@/hooks/useApi/useRequestClientPasswordReset';
import useVerifyRecaptcha from '@/hooks/useApi/useVerifyRecaptcha';
import { useTranslation } from 'react-i18next';

const { TextField, Button: TextButton, Box, Title, Icon } = vp;

type ResetPasswordProps = {
  handleResetPassword: (e: boolean) => void;
  handleLoginPage: (e: boolean) => void;
};

type FC = FunctionComponent<ResetPasswordProps>;

// Public site key per environment (VITE_RECAPTCHA_SITE_KEY); the backend verifies with the
// matching secret. Manage keys/allowed domains at https://www.google.com/recaptcha/admin
const RECAPTCHA_SITE_KEY: string = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

const ResetPassword: FC = (props: ResetPasswordProps) => {
  const { handleResetPassword, handleLoginPage } = props;

  const { requestPasswordReset } = useRequestClientPasswordReset();
  const { verifyRecaptcha } = useVerifyRecaptcha();
  // Fullscreen, blocking: the user must not resubmit or navigate while a request is in flight.
  const [, setBlocking] = useDataFetchIndicator();
  const [shouldShowValidateError, setShouldShowValidateError] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false);
  const [requestErrorMsg, setRequestErrorMsg] = useState('');
  const captchaRef = useRef<ReCAPTCHA>(null);
  const { t } = useTranslation('translations', { keyPrefix: 'Login.ResetPassword' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });
  const { t: tLogin } = useTranslation('translations', { keyPrefix: 'Login.Form' });

  const sendResetEmail = async (value: vp.ClientResetPasswordEmailRequest) => {
    if (!isRecaptchaVerified) {
      setShouldShowValidateError(true);

      return;
    }

    try {
      setBlocking(true);
      setRequestErrorMsg('');
      // Pasted/autofilled usernames often carry whitespace; the endpoint answers Success either
      // way (no enumeration), so a stray space would otherwise be a silent dead end.
      await requestPasswordReset({ Username: value.Username.trim() });
      // Only after the backend confirms: an optimistic "Email Sent" hides real failures.
      setIsEmailSent(true);
    } catch (error) {
      setRequestErrorMsg(errorMessage(error, t('Form.requestError')));
    } finally {
      setBlocking(false);
    }
  };

  const onChange = async () => {
    setShouldShowValidateError(false);

    // reCAPTCHA also fires onChange with no token when one expires; there is
    // nothing to verify then, so leave the form enabled for another attempt.
    const response = captchaRef.current?.getValue();

    if (!response) {
      setIsRecaptchaVerified(false);

      return;
    }

    try {
      setBlocking(true);
      setRequestErrorMsg('');
      await verifyRecaptcha({ response });
      setIsRecaptchaVerified(true);
    } catch (error) {
      setIsRecaptchaVerified(false);
      captchaRef.current?.reset();
      setRequestErrorMsg(errorMessage(error, t('Form.recaptchaError')));
    } finally {
      setBlocking(false);
    }
  };

  return (
    <>
      {isEmailSent ? (
        <Box flx="col">
          <Box mb="sm" flx="col" ai="center" height="auto">
            <Title title={t('Resume.title')} bold size="xsm" />
          </Box>

          <div className={styles.errorText}>{t('Resume.description')}</div>

          <Box jc="fe" flx="col" height="auto">
            <TextButton
              id="return-to-login-page"
              type="button"
              label={t('Resume.returnButton')}
              textSize={1}
              onClick={() => {
                setIsEmailSent(false);
                handleResetPassword(false);
                handleLoginPage(true);
              }}
            />
          </Box>
        </Box>
      ) : (
        <Box flx="col" width="100%">
          <Box mb="sm" flx="col" ai="center" height="auto">
            <Title title={t('Form.title')} bold size="xsm" />
          </Box>

          <Formik
            initialValues={{ Username: '' }}
            validationSchema={resetPasswordValidationSchema()}
            onSubmit={sendResetEmail}
          >
            {({ values, handleChange, handleBlur, handleSubmit, errors, touched, isSubmitting }) => (
              <form onSubmit={handleSubmit}>
                <Box mb="sm" flx="col" height="auto">
                  <div className={styles.fieldLabel}>{tLogin('usernameLabel')}</div>

                  <TextField
                    key="Username"
                    name="Username"
                    placeholder={tLogin('usernamePlaceholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.Username}
                    variant="outlineShadow"
                    elementLeft={<Icon icon="user" size="md" color="medium-dark" />}
                    error={errors.Username && touched.Username ? errors.Username : null}
                  />
                </Box>

                <div className={styles.reCAPTCHAContainer}>
                  <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={onChange} ref={captchaRef} />
                  {shouldShowValidateError && <div className={styles.errorMSG}>{t('Form.notARobotValidation')}</div>}
                </div>

                {requestErrorMsg && <div className={styles.errorMSG}>{requestErrorMsg}</div>}

                <Box jc="fe" flx="col" height="auto">
                  <Button
                    id="reset-password-submit"
                    type="submit"
                    colorScheme="primary"
                    size="large"
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {t('Form.submitButton')}
                  </Button>
                </Box>
              </form>
            )}
          </Formik>

          <Box mt="sm" height="auto" jc="center" ai="center">
            <TextButton
              id="forgot-password-cancel"
              type="button"
              label={tGlobal('goBack', { defaultValue: 'Go back' })}
              color="none"
              noShadow
              noPadding
              bold
              textSize={1}
              onClick={() => {
                handleResetPassword(false);
                handleLoginPage(true);
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );

  // The backend's reason is the useful one, but it comes back blank on a transport failure.
  function errorMessage(error: unknown, fallback: string) {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  function resetPasswordValidationSchema() {
    return Yup.object().shape({
      Username: Yup.string().label(t('Form.usernameLabel')).required(),
    });
  }
};

export default ResetPassword;
